/**
 * RecordDetailPanel - Slide-out panel for viewing record details
 */

import React, { useEffect, useRef, useState } from 'react';
import { X, ChevronDown, ChevronRight } from 'lucide-react';

interface RecordDetailPanelProps {
  record: Record<string, any> | null;
  onClose: () => void;
  accentColor?: string;
}

/** Fields considered primary — shown at top */
const PRIMARY_FIELDS = [
  'title', 'name', 'region', 'station', 'authors', 'journal',
  'year', 'date', 'category', 'type', 'country', 'description',
  'price', 'observation', 'facility_type', 'jurisdiction',
  'citations', 'source',
];

/** Fields to hide from the detail view */
const HIDDEN_FIELDS = ['embedding', 'vector', 'embedding_vector', '_score'];

const RecordDetailPanel: React.FC<RecordDetailPanelProps> = ({ record, onClose, accentColor = '#111827' }) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [showRaw, setShowRaw] = useState(false);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    // Delay to avoid closing immediately from the click that opened the panel
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClick);
    }, 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  if (!record) return null;

  const allKeys = Object.keys(record).filter(k => !HIDDEN_FIELDS.includes(k));
  const primaryKeys = allKeys.filter(k => PRIMARY_FIELDS.includes(k));
  const metadataKeys = allKeys.filter(k => !PRIMARY_FIELDS.includes(k));

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return '—';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/30 z-40" />

      {/* Panel */}
      <div
        ref={panelRef}
        className="fixed top-0 right-0 h-full w-full max-w-lg bg-gray-100 shadow-2xl z-50 flex flex-col animate-slide-in-right"
        style={{ animation: 'slideInRight 0.2s ease-out' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" style={{ backgroundColor: accentColor + '10' }}>
          <h3 className="text-lg font-semibold text-gray-900 truncate pr-4">
            {record.title || record.name || record.id || 'Record Details'}
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Primary fields */}
          {primaryKeys.length > 0 && (
            <div className="space-y-3 mb-6">
              {primaryKeys.map(key => (
                <div key={key}>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">{key.replace(/_/g, ' ')}</dt>
                  <dd className="mt-0.5 text-sm text-gray-900 whitespace-pre-wrap">{formatValue(record[key])}</dd>
                </div>
              ))}
            </div>
          )}

          {/* Metadata fields */}
          {metadataKeys.length > 0 && (
            <>
              <div className="border-t border-gray-100 pt-4 mb-3">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Metadata</h4>
              </div>
              <div className="space-y-3 mb-6">
                {metadataKeys.map(key => (
                  <div key={key}>
                    <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">{key.replace(/_/g, ' ')}</dt>
                    <dd className="mt-0.5 text-sm text-gray-900 whitespace-pre-wrap break-all">{formatValue(record[key])}</dd>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Raw data (collapsed) */}
          <div className="border-t border-gray-100 pt-4">
            <button
              onClick={() => setShowRaw(!showRaw)}
              className="flex items-center gap-2 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
            >
              {showRaw ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              Raw Record Data
            </button>
            {showRaw && (
              <pre className="mt-2 p-3 bg-gray-50 rounded-lg text-xs text-gray-700 overflow-x-auto max-h-64 border border-gray-200">
                {JSON.stringify(record, null, 2)}
              </pre>
            )}
          </div>
        </div>
      </div>

      {/* Animation keyframes */}
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
};

export default RecordDetailPanel;
